import React from "react";
import { NewsletterForm } from "@/components/NewsletterForm";
import { useTranslation } from "@/i18n/I18nProvider";

export default function Home() {
  const { t, lang } = useTranslation();

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-blue-900 mb-4">
              {t("hero.badge")}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              {t("hero.title")}
            </h1>
            <p className="text-base lg:text-lg text-slate-600 leading-relaxed mt-6 max-w-2xl">
              {t("hero.subtitle")}
            </p>

            <ul className="mt-8 space-y-3 max-w-2xl">
              {["bullet_1", "bullet_2", "bullet_3"].map((k) => (
                <li key={k} className="flex items-start gap-3 text-slate-800">
                  <span className="mt-1.5 inline-block h-1.5 w-4 bg-amber-500 flex-shrink-0" />
                  <span className="text-sm md:text-base">{t(`hero.${k}`)}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:hidden">
              <img
                src={`${process.env.PUBLIC_URL || ""}/images/hero.jpg`}
                alt={t("hero.image_alt")}
                className="w-full h-64 object-cover rounded-xl border border-slate-200"
                loading="eager"
              />
            </div>
          </div>

          <div className="lg:col-span-5" id="newsletter">
            <div className="hidden lg:block mb-6">
              <img
                src={`${process.env.PUBLIC_URL || ""}/images/hero.jpg`}
                alt={t("hero.image_alt")}
                className="w-full h-56 object-cover rounded-xl border border-slate-200"
                loading="eager"
              />
            </div>
            <NewsletterForm variant="hero" />
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section id="expertise" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-blue-900 mb-3">
              {t("pillars.eyebrow")}
            </p>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("pillars.title")}
            </h2>
            <p className="text-slate-600 mt-4">{t("pillars.subtitle")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {["gov", "trans", "clim"].map((key) => (
              <article
                key={key}
                className="bg-slate-50 border border-slate-200 rounded-xl p-6"
                data-testid={`pillar-${key}`}
              >
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-semibold border border-amber-200 bg-amber-50 rounded-full px-2.5 py-1">
                  {t(`pillars.${key}.code`)}
                </div>
                <h3 className="text-lg md:text-lg font-semibold text-slate-900 mt-4">
                  {t(`pillars.${key}.title`)}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">
                  {t(`pillars.${key}.description`)}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Proof / stats */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 lg:py-16">
          <h2 className="text-2xl lg:text-3xl font-semibold text-white tracking-tight">
            {t("proof.title")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
            {[1, 2, 3].map((n) => (
              <div key={n} className="border-l-4 border-amber-500 pl-5" data-testid={`stat-${n}`}>
                <div className="text-4xl lg:text-5xl font-bold text-white">
                  {t(`proof.stat${n}_value`)}
                </div>
                <div className="text-sm text-slate-300 mt-2">
                  {t(`proof.stat${n}_label`)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Editorial */}
      <section id="insights" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-[0.24em] font-semibold text-blue-900 mb-3">
              {t("editorial.eyebrow")}
            </p>
            <h2 className="text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight">
              {t("editorial.title")}
            </h2>
            <p className="text-slate-600 leading-relaxed mt-5">{t("editorial.body_1")}</p>
            <p className="text-slate-600 leading-relaxed mt-4">{t("editorial.body_2")}</p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <img
              src={`${process.env.PUBLIC_URL || ""}/images/boardroom.jpg`}
              alt={t("editorial.image_alt")}
              className="w-full h-72 lg:h-[420px] object-cover rounded-xl border border-slate-200"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14 lg:py-20 text-center">
          <h2 className="text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight">
            {t("cta_footer.title")}
          </h2>
          <p className="text-slate-600 mt-4 max-w-2xl mx-auto">{t("cta_footer.subtitle")}</p>
          <a
            href="#newsletter"
            className="inline-block mt-8 bg-slate-900 text-white font-semibold px-6 py-3 rounded-md hover:bg-slate-800 text-sm"
            data-testid="cta-newsletter-anchor"
          >
            {t("cta_footer.button")}
          </a>
        </div>
      </section>
    </div>
  );
}
